import React from "react";
import Helmet from "react-helmet";
import {
  default_description,
  default_title,
  default_keywords,
  site_name,
  site_url,
  twitter_handle,
  og_image,
  favicon_32,
  favicon_16,
  apple_touch_icon,
} from "../utils/constants/meta";

function Meta({ pageMeta, children }) {
  const defaultMeta = {
    title: default_title,
    description: default_description,
    keywords: default_keywords,
    type: "website",
    image: og_image,
    url: site_url,
  };

  const meta = {
    ...defaultMeta,
    ...pageMeta,
  };

  const fullTitle = meta.title.includes(site_name) ? meta.title : `${meta.title} | ${site_name}`;
  const currentUrl = meta.url || `${site_url}${window.location.pathname}`;

  return (
    <>
      <Helmet defer={true}>
        {/* Basic Meta Tags */}
        <title>{fullTitle}</title>
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes"
        />
        <meta name="description" content={meta.description} />
        <meta name="author" content="Doitly Inc." />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#3b82f6" />

        {/* Keywords */}
        {meta.keywords && (
          <meta name="keywords" content={meta.keywords.join(", ")} />
        )}

        {/* Favicon and Icons */}
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href={favicon_32} />
        <link rel="icon" type="image/png" sizes="16x16" href={favicon_16} />
        <link rel="apple-touch-icon" href={apple_touch_icon} />

        {/* Open Graph Tags */}
        <meta property="og:type" content={meta.type} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={meta.image} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:site_name" content={site_name} />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={twitter_handle} />
        <meta name="twitter:creator" content={twitter_handle} />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        {/* Additional SEO */}
        <meta name="application-name" content={site_name} />
        <meta name="msapplication-TileColor" content="#3b82f6" />
        <meta name="format-detection" content="telephone=no" />

        {/* Canonical URL */}
        <link rel="canonical" href={currentUrl} />

        {/* Preconnect for Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="//www.googletagmanager.com" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": site_name,
            "url": site_url,
            "description": meta.description,
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${site_url}/tasks?query={search_term_string}`,
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      <div style={{ width: "100%" }}>{children}</div>
    </>
  );
}

export default Meta;
